import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Pie, Bar } from "react-chartjs-2";
import { apiurl } from "../config/config";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import {
  Search,
  ArrowUpDown,
  CheckCircle,
  XCircle,
  ExternalLink,
  List,
  User,
  FileText,
  ChevronRight,
  ChevronLeft,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import Layout, { Container } from "../components/layout";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

/**
 * DocumentSummaryV2
 * Single-page dashboard: summary cards, document cards, charts, employee table, drawer.
 */
export default function DocumentSummaryV2() {
  const [summary, setSummary] = useState(null);
  const [documentWise, setDocumentWise] = useState(null);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);

  // Table states
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("rating"); // rating | missing | uploaded | name
  const [page, setPage] = useState(1);
  const perPage = 100;

  // Drawer state
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activeEmployee, setActiveEmployee] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await axios.post(
          `${apiurl}/api/employee/get-missing-documents-report`
        );
        if (res.data?.success) {
          setSummary(res.data.summary);
          setDocumentWise(res.data.documentWiseTotals);
          setReport(res.data.report || []);
          toast.success("Report loaded");
        } else {
          toast.error(res.data?.message || "Failed to load report");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error fetching report");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Derived data: array of document cards from object
  const documentCards = useMemo(() => {
    if (!documentWise) return [];
    return Object.entries(documentWise).map(([key, val]) => ({
      key,
      title: val.name,
      uploaded: val.uploaded,
      pending: val.pending,
    }));
  }, [documentWise]);

  // prepare charts
  const pieData = useMemo(() => {
    if (!summary) return null;
    return {
      labels: ["Uploaded", "Pending"],
      datasets: [
        {
          data: [summary.totalDocumentsUploaded, summary.totalDocumentsPending],
        },
      ],
    };
  }, [summary]);

  const barData = useMemo(() => {
    if (!documentCards.length) return null;
    return {
      labels: documentCards.map((d) => d.title),
      datasets: [
        {
          label: "Uploaded",
          data: documentCards.map((d) => d.uploaded),
        },
        {
          label: "Pending",
          data: documentCards.map((d) => d.pending),
        },
      ],
    };
  }, [documentCards]);

  // Table: search + sort
  const filtered = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    let list = report.filter((r) => {
      if (!q) return true;
      return (
        (r.employeeName || "").toLowerCase().includes(q) ||
        (r.employeeCode || "").toLowerCase().includes(q) ||
        (r.employeeId || "").toLowerCase().includes(q)
      );
    });

    list.sort((a, b) => {
      if (sortBy === "missing") return b.missingCount - a.missingCount;
      if (sortBy === "uploaded") return b.uploadedCount - a.uploadedCount;
      if (sortBy === "name")
        return (a.employeeName || "").localeCompare(b.employeeName || "");
      // default rating
      return b.rating - a.rating;
    });

    return list;
  }, [report, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  // Drawer open
  function openEmployeeDrawer(emp) {
    setActiveEmployee(emp);
    setOpenDrawer(true);
  }

  // Helper: show document status badge
  function DocBadge({ uploaded }) {
    return uploaded ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-100">
        <CheckCircle className="w-3 h-3" /> Uploaded
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-100">
        <XCircle className="w-3 h-3" /> Pending
      </span>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        {/* <Toaster /> */}
        <div className="text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (!summary || !documentWise) {
    return (
      <div className="p-6 text-red-600">
        {/* <Toaster /> */}
        No data available.
      </div>
    );
  }

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
      {/* <Toaster position="top-right" /> */}

      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Document Summary</h1>
            <p className="text-sm text-gray-500">
              Total employees: <span className="font-bold">{summary.totalEmployees}</span> · Completion:{" "}
              <span className="font-bold">{summary.overallUploadPercentage}%</span>
            </p>
          </div>

          {/* Search + sort */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search name, code or ID..."
                className="pl-10 pr-3 py-2 rounded-lg border w-64 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="py-2 px-3 border rounded-lg text-sm"
              >
                <option value="rating">Rating (high → low)</option>
                <option value="missing">Missing (high → low)</option>
                <option value="uploaded">Uploaded (high → low)</option>
                <option value="name">Name (A → Z)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <SummaryCard title="Total Uploaded" value={summary.totalDocumentsUploaded} color="green" />
          <SummaryCard title="Total Pending" value={summary.totalDocumentsPending} color="red" />
          <SummaryCard title="Completion %" value={`${summary.overallUploadPercentage}%`} color="blue" />
          <SummaryCard title="Total Employees" value={summary.totalEmployees} color="purple" />
        </div>

        {/* Document Cards + Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: document cards grid */}
          <div className="lg:col-span-1 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {documentCards.map((d) => (
                <div key={d.key} className="bg-white p-4 rounded-lg shadow-sm border">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm text-gray-500">{d.title}</div>
                      <div className="text-lg font-semibold mt-1">
                        {d.uploaded} <span className="text-sm text-gray-400">/ {d.uploaded + d.pending}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-green-600 font-semibold">{d.uploaded}</div>
                      <div className="text-sm text-red-600">{d.pending} pending</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* quick actions */}
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Quick Actions</div>
                <div className="text-xs text-gray-500">Admin</div>
              </div>
              <div className="flex gap-2">
                <button
                  className="flex-1 py-2 rounded bg-blue-600 text-white text-sm"
                  onClick={() => {
                    toast.promise(
                      axios.post(`${apiurl}/api/employee/resend-upload-links`),
                      {
                        loading: "Sending links...",
                        success: "Resend started",
                        error: "Failed to start resend",
                      }
                    );
                  }}
                >
                  Resend Upload Links
                </button>
                <button
                  className="py-2 px-3 rounded border text-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success("Dashboard URL copied");
                  }}
                >
                  Share
                </button>
              </div>
            </div>
          </div>

          {/* Center: charts */}
          <div className="lg:col-span-2 bg-white p-4 rounded-lg shadow-sm border">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-3">
                <h3 className="text-sm font-semibold mb-2 text-center">Overall Upload Status</h3>
                <div className="w-full max-w-md mx-auto">
                  <Pie data={pieData} />
                </div>
              </div>

              <div className="p-3">
                <h3 className="text-sm font-semibold mb-2 text-center">Document-wise Summary</h3>
                <div className="w-full">
                  <Bar data={barData} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Employee table */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Employees ({filteredLengthText(filteredLength(filtered))})</h2>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">Showing {pageItems.length} / {filtered.length}</div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-left">Employee</th>
                  <th className="p-3 text-center">Uploaded</th>
                  <th className="p-3 text-center">Missing</th>
                  <th className="p-3 text-center">Rating</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((emp) => (
                  <tr key={emp.employeeObjId} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="font-medium">{emp.employeeName}</div>
                      <div className="text-xs text-gray-500">{emp.employeeCode} • ID: {emp.employeeId}</div>
                    </td>
                    <td className="p-3 text-center text-green-600 font-semibold">{emp.uploadedCount}</td>
                    <td className="p-3 text-center text-red-600 font-semibold">{emp.missingCount}</td>
                    <td className="p-3 text-center">
                      <span className={`px-3 py-1 rounded text-white ${emp.rating >= 80 ? "bg-green-600" : emp.rating >= 50 ? "bg-yellow-500" : "bg-red-600"}`}>
                        {emp.rating}%
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          className="px-3 py-1 bg-blue-600 text-white rounded text-xs flex items-center gap-2"
                          onClick={() => openEmployeeDrawer(emp)}
                        >
                          <List className="w-4 h-4" /> View
                        </button>
                        <a
                        //   href={`/employees/profile/${emp.employeeObjId}`}
                          href={`/employees/${emp.employeeObjId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2 py-1 border rounded text-xs flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                className="px-3 py-1 rounded border disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="inline-flex gap-1">
                {Array.from({ length: totalPages }).slice(Math.max(0, page - 3), Math.min(totalPages, page + 2)).map((_, idx) => {
                  const pageNum = idx + Math.max(1, page - 2);
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 rounded ${pageNum === page ? "bg-blue-600 text-white" : "border"}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                disabled={page === totalPages}
                className="px-3 py-1 rounded border disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <EmployeeDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        employee={activeEmployee}
      />
          </Container>
    </Layout>
  );
}

/* ---------- Helper components ---------- */

function SummaryCard({ title, value, color = "gray" }) {
  const colorMap = {
    green: "text-green-600",
    red: "text-red-600",
    blue: "text-blue-600",
    purple: "text-purple-600",
    gray: "text-gray-700",
  };
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      <div className="text-sm text-gray-500">{title}</div>
      <div className={`text-2xl font-semibold ${colorMap[color] || colorMap.gray}`}>{value}</div>
    </div>
  );
}

function EmployeeDrawer({ open, onClose, employee }) {
  if (!open || !employee) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* backdrop */}
      <div onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="relative ml-auto w-full max-w-md bg-white h-full shadow-2xl overflow-auto">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">{employee.employeeName}</div>
            <div className="text-xs text-gray-500">{employee.employeeCode} • ID: {employee.employeeId}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <div className="text-sm font-medium mb-2">Document Checklist</div>
            <div className="space-y-2">
              {employee.documents.map((d) => (
                <div key={d.key} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <div>
                      <div className="text-sm">{d.name}</div>
                      <div className="text-xs text-gray-400">{d.verificationStatus}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <DocBadge uploaded={d.uploaded} />
                    {d.uploaded && d.link && (
                      <a href={d.link} target="_blank" rel="noreferrer" className="text-sm text-blue-600 flex items-center gap-1">
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Actions</div>
            <div className="flex gap-2">
              <a
                href={`/employees/${employee.employeeObjId}`}
                // href={`/employees/profile/${employee.employeeObjId}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-2 rounded bg-blue-600 text-white text-sm flex items-center gap-2"
              >
                View Profile
              </a>

              <button
                className="px-3 py-2 rounded border text-sm"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(employee.documents, null, 2));
                  toast.success("Document JSON copied");
                }}
              >
                Copy Doc JSON
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* small helpers used above */
function DocBadge({ uploaded }) {
  return uploaded ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-100">
      <CheckCircle className="w-3 h-3" /> Uploaded
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-100">
      <XCircle className="w-3 h-3" /> Pending
    </span>
  );
}

/* small util to show count nicely */
function filteredLengthText(n) {
  return `${n} results`;
}

/* expose filtered length for header (used above) */
function filteredLength(filtered) {
  return filtered.length;
}